/**
 * Definições de servidor. É o painel de administração: visão geral, roles
 * (com o bitfield de permissões à mão), membros (expulsar, banir, atribuir
 * roles) e convites activos.
 *
 * Cada separador vai buscar os seus próprios dados e trata das mutações contra
 * a API; o eco do gateway trata de propagar para o resto do cliente. A UI só
 * mostra o que a permissão do próprio deixa — o servidor é que decide de facto.
 */

import type { AuditEntry, Emoji, Invite, Member, PublicUser, Role, Snowflake } from "@greenvoice/shared";
import { deserialize, EVERYONE_RANK, Limits, type PERMISSION_NAMES, Permission, serialize } from "@greenvoice/shared";
import { useEffect, useMemo, useRef, useState } from "react";
import type { BanEntry } from "../lib/api";
import { api } from "../lib/api";
import type { Nomeador } from "../lib/auditoria";
import { ACCOES_CONHECIDAS, accaoDe, alteracoesDe, alvoDe } from "../lib/auditoria";
import { formatMessageStamp } from "../lib/format";
import { GRUPOS_DE_PERMISSOES, NOME_DA_PERMISSAO } from "../lib/permissoes";
import { uploadImage } from "../lib/upload";
import { can, emojisOf, selfPermissions } from "../store/selectors";
import { useStore } from "../store/store";
import { Avatar } from "./Avatar";
import { IconChevron, IconClose, IconPlus, IconTrash } from "./Icons";

export type { ServerSettingsTab as SettingsTab } from "../store/serverSettingsTab";

type SettingsTab = import("../store/serverSettingsTab").ServerSettingsTab;
type Tab = SettingsTab;

export function ServerSettings({
  serverId,
  onClose,
  initialTab,
}: {
  serverId: Snowflake;
  onClose: () => void;
  initialTab?: SettingsTab;
}) {
  const state = useStore();
  const permissions = selfPermissions(state, serverId);
  const server = state.servers[serverId];

  const canOverview = can(permissions, Permission.MANAGE_SERVER);
  const canRoles = can(permissions, Permission.MANAGE_ROLES);
  const canMembers =
    can(permissions, Permission.KICK_MEMBERS) ||
    can(permissions, Permission.BAN_MEMBERS) ||
    can(permissions, Permission.ASSIGN_ROLES);
  const canInvites = can(permissions, Permission.MANAGE_SERVER);
  const canBans = can(permissions, Permission.BAN_MEMBERS);
  // O servidor exige VIEW_AUDIT_LOG neste endpoint, não MANAGE_SERVER —
  // separador aberto pelo bit errado é um separador que responde 403.
  const canAudit = can(permissions, Permission.VIEW_AUDIT_LOG);
  const canEmojis = can(permissions, Permission.MANAGE_EMOJIS);

  const tabs: { id: Tab; label: string; show: boolean }[] = [
    { id: "overview", label: "Visão geral", show: canOverview },
    { id: "roles", label: "Cargos", show: canRoles },
    { id: "members", label: "Membros", show: canMembers },
    { id: "emojis", label: "Emojis", show: canEmojis },
    { id: "bans", label: "Banidos", show: canBans },
    { id: "audit", label: "Auditoria", show: canAudit },
    { id: "invites", label: "Convites", show: canInvites },
  ];
  const visible = tabs.filter((t) => t.show);
  const wantedTab = initialTab && visible.some((t) => t.id === initialTab) ? initialTab : undefined;
  const [tab, setTab] = useState<Tab>(wantedTab ?? visible[0]?.id ?? "overview");

  if (!server) return null;

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label={`Configurações de ${server.name}`}>
      <div className="modal__panel settings__panel">
        <header className="modal__head">
          <h2>{server.name}</h2>
          <button type="button" className="icon-button" onClick={onClose} title="Fechar">
            <IconClose size={18} />
          </button>
        </header>

        <nav className="settings__tabs" role="tablist">
          {visible.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`settings__tab${tab === t.id ? " is-active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div className="settings__body">
          {tab === "overview" && canOverview ? <OverviewTab serverId={serverId} /> : null}
          {tab === "roles" && canRoles ? <RolesTab serverId={serverId} /> : null}
          {tab === "members" && canMembers ? (
            <MembersTab
              serverId={serverId}
              canAssign={can(permissions, Permission.ASSIGN_ROLES)}
              canKick={can(permissions, Permission.KICK_MEMBERS)}
              canBan={can(permissions, Permission.BAN_MEMBERS)}
            />
          ) : null}
          {tab === "emojis" && canEmojis ? <EmojisTab serverId={serverId} /> : null}
          {tab === "bans" && canBans ? <BansTab serverId={serverId} /> : null}
          {tab === "audit" && canAudit ? <AuditTab serverId={serverId} /> : null}
          {tab === "invites" && canInvites ? <InvitesTab serverId={serverId} /> : null}
        </div>
      </div>
    </div>
  );
}

/* ── visão geral ──────────────────────────────────────────── */

function OverviewTab({ serverId }: { serverId: Snowflake }) {
  const server = useStore((s) => s.servers[serverId])!;
  const [name, setName] = useState(server.name);
  const [description, setDescription] = useState(server.description ?? "");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.servers.update(serverId, {
        name: name.trim(),
        description: description.trim() || null,
      });
      setNotice("Salvo.");
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="settings__form"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <label className="field">
        <span>Nome do servidor</span>
        <input value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="field">
        <span>Descrição</span>
        <textarea value={description} maxLength={1024} rows={3} onChange={(e) => setDescription(e.target.value)} />
      </label>
      <div className="settings__foot">
        <button type="submit" className="button button--primary" disabled={busy || !name.trim()}>
          Salvar
        </button>
        {notice ? (
          <span className="settings__notice" role="status">
            {notice}
          </span>
        ) : null}
      </div>
      {error ? (
        <p className="auth__error" role="alert">
          {error}
        </p>
      ) : null}
    </form>
  );
}

/* ── roles ────────────────────────────────────────────────── */

function RolesTab({ serverId }: { serverId: Snowflake }) {
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedId, setSelectedId] = useState<Snowflake | null>(null);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setBusy(true);
    setError(null);
    try {
      const list = await api.servers.roles(serverId);
      setRoles([...list].sort((a, b) => a.rank - b.rank));
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverId]);

  const manageable = roles.filter((r) => !r.isDefault);

  async function createRole() {
    if (!newName.trim()) return;
    try {
      const role = await api.servers.createRole(serverId, { name: newName.trim() });
      setNewName("");
      setRoles((prev) => [...prev, role].sort((a, b) => a.rank - b.rank));
      setSelectedId(role.id);
    } catch (caught) {
      setError((caught as Error).message);
    }
  }

  async function move(id: Snowflake, dir: "up" | "down") {
    const i = manageable.findIndex((r) => r.id === id);
    const j = dir === "up" ? i - 1 : i + 1;
    const a = manageable[i];
    const b = manageable[j];
    if (!a || !b) return;
    const next = [...manageable];
    next[i] = b;
    next[j] = a;
    try {
      await api.servers.reorderRoles(
        serverId,
        next.map((r, idx) => ({ id: r.id, rank: idx })),
      );
      await load();
    } catch (caught) {
      setError((caught as Error).message);
    }
  }

  const selected = roles.find((r) => r.id === selectedId) ?? null;

  if (busy && roles.length === 0) return <p className="modal__hint">Carregando…</p>;

  return (
    <div className="roles">
      <div className="roles__list">
        <div className="roles__new">
          <input
            value={newName}
            placeholder="Novo cargo"
            maxLength={100}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void createRole();
              }
            }}
          />
          <button type="button" className="icon-button" onClick={() => void createRole()} title="Criar cargo">
            <IconPlus size={18} />
          </button>
        </div>

        {manageable.map((role, idx) => (
          <div key={role.id} className={`roles__row${selectedId === role.id ? " is-active" : ""}`}>
            <button type="button" className="roles__pick" onClick={() => setSelectedId(role.id)}>
              <span className="roles__dot" style={{ background: role.color ?? "var(--text-dim)" }} />
              <span className="roles__label">{role.name}</span>
            </button>
            <div className="roles__move">
              <button
                type="button"
                className="icon-button"
                disabled={idx === 0}
                onClick={() => void move(role.id, "up")}
                title="Subir"
              >
                <IconChevron size={14} className="roles__up" />
              </button>
              <button
                type="button"
                className="icon-button"
                disabled={idx === manageable.length - 1}
                onClick={() => void move(role.id, "down")}
                title="Descer"
              >
                <IconChevron size={14} />
              </button>
            </div>
          </div>
        ))}

        {roles
          .filter((r) => r.isDefault)
          .map((role) => (
            <button
              key={role.id}
              type="button"
              className={`roles__row roles__pick${selectedId === role.id ? " is-active" : ""}`}
              onClick={() => setSelectedId(role.id)}
            >
              <span className="roles__dot" style={{ background: role.color ?? "var(--text-dim)" }} />
              <span className="roles__label">{role.name}</span>
              <span className="tag">base</span>
            </button>
          ))}
      </div>

      <div className="roles__editor">
        {selected ? (
          <RoleEditor
            key={selected.id}
            serverId={serverId}
            role={selected}
            onSaved={(updated) =>
              setRoles((prev) => prev.map((r) => (r.id === updated.id ? updated : r)).sort((a, b) => a.rank - b.rank))
            }
            onDeleted={(id) => {
              setRoles((prev) => prev.filter((r) => r.id !== id));
              setSelectedId(null);
            }}
          />
        ) : (
          <p className="modal__hint">Escolha um cargo à esquerda, ou crie um novo.</p>
        )}
      </div>

      {error ? (
        <p className="auth__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function RoleEditor({
  serverId,
  role,
  onSaved,
  onDeleted,
}: {
  serverId: Snowflake;
  role: Role;
  onSaved: (role: Role) => void;
  onDeleted: (id: Snowflake) => void;
}) {
  const [name, setName] = useState(role.name);
  const [color, setColor] = useState(role.color ?? "");
  const [hoist, setHoist] = useState(role.hoist);
  const [mentionable, setMentionable] = useState(role.mentionable);
  const [bits, setBits] = useState<bigint>(deserialize(role.permissions));
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function toggle(name: (typeof PERMISSION_NAMES)[number]) {
    const bit = Permission[name];
    setBits((prev) => (prev & bit ? prev & ~bit : prev | bit));
  }

  async function save() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const updated = await api.servers.updateRole(serverId, role.id, {
        name: name.trim(),
        color: color.trim() || null,
        hoist,
        mentionable,
        permissions: serialize(bits),
      });
      setNotice("Cargo salvo.");
      onSaved(updated);
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await api.servers.deleteRole(serverId, role.id);
      onDeleted(role.id);
    } catch (caught) {
      setError((caught as Error).message);
      setBusy(false);
    }
  }

  return (
    <form
      className="settings__form"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <div className="roles__grid">
        <label className="field">
          <span>Nome</span>
          <input value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="field">
          <span>Cor (hex)</span>
          <input value={color} placeholder="#5865F2" maxLength={7} onChange={(e) => setColor(e.target.value)} />
        </label>
      </div>

      <div className="roles__flags">
        <label className="check">
          <input type="checkbox" checked={hoist} onChange={(e) => setHoist(e.target.checked)} />
          <span>Mostrar em separado na lista de membros</span>
        </label>
        <label className="check">
          <input type="checkbox" checked={mentionable} onChange={(e) => setMentionable(e.target.checked)} />
          <span>Pode ser mencionada</span>
        </label>
      </div>

      {/* Por assunto e em português (ver `lib/permissoes`). Eram 33 caixas em
          fila com o nome do BIT em inglês — "Manage roles", "Read message
          history" — na única vista da app onde se decide quem pode expulsar,
          banir e apagar. Uma parede, e numa língua que não é a de ninguém aqui. */}
      {GRUPOS_DE_PERMISSOES.map((grupo) => (
        <div key={grupo.titulo}>
          <p className="modal__hint">{grupo.titulo}</p>
          <div className="perms">
            {grupo.permissoes.map((perm) => (
              <label key={perm} className="check">
                <input type="checkbox" checked={(bits & Permission[perm]) !== 0n} onChange={() => toggle(perm)} />
                <span>{NOME_DA_PERMISSAO[perm]}</span>
              </label>
            ))}
          </div>
        </div>
      ))}

      <div className="settings__foot">
        <button type="submit" className="button button--primary" disabled={busy || !name.trim()}>
          Salvar
        </button>
        {!role.isDefault ? (
          <button
            type="button"
            className="button button--small icon-button--danger"
            disabled={busy}
            onClick={() => void remove()}
          >
            <IconTrash size={16} /> Excluir
          </button>
        ) : null}
        {notice ? (
          <span className="settings__notice" role="status">
            {notice}
          </span>
        ) : null}
      </div>

      {error ? (
        <p className="auth__error" role="alert">
          {error}
        </p>
      ) : null}
    </form>
  );
}

/* ── membros ──────────────────────────────────────────────── */

function MembersTab({
  serverId,
  canAssign,
  canKick,
  canBan,
}: {
  serverId: Snowflake;
  canAssign: boolean;
  canKick: boolean;
  canBan: boolean;
}) {
  const self = useStore((s) => s.self);
  const ownerId = useStore((s) => s.servers[serverId]?.ownerId);
  const [members, setMembers] = useState<Member[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Snowflake | null>(null);
  // Banir é definitivo enquanto ninguém desbanir: pede motivo antes, e o
  // motivo é o que aparece na lista de banidos e no registo de auditoria.
  const [banindo, setBanindo] = useState<Snowflake | null>(null);
  const [motivo, setMotivo] = useState("");
  const [dias, setDias] = useState(0);

  useEffect(() => {
    let alive = true;
    setBusy(true);
    setError(null);
    Promise.all([api.servers.members(serverId, { limit: 100 }), api.servers.roles(serverId)])
      .then(([memberList, roleList]) => {
        if (!alive) return;
        setMembers(memberList);
        setRoles(roleList);
      })
      .catch((caught) => {
        if (alive) setError((caught as Error).message);
      })
      .finally(() => {
        if (alive) setBusy(false);
      });
    return () => {
      alive = false;
    };
  }, [serverId]);

  const roleById = new Map(roles.map((r) => [r.id, r]));
  const rankOf = (member: Member): number => {
    let min = EVERYONE_RANK;
    for (const id of member.roleIds) {
      const r = roleById.get(id);
      if (r && r.rank < min) min = r.rank;
    }
    return min;
  };

  const isOwner = self?.id === ownerId;
  const selfMember = members.find((m) => m.user.id === self?.id);
  const actorRank = selfMember ? rankOf(selfMember) : EVERYONE_RANK;

  const canModerate = (member: Member): boolean => {
    if (member.user.id === self?.id) return false;
    if (member.user.id === ownerId) return false;
    return isOwner || actorRank < rankOf(member);
  };

  async function assignRole(member: Member, roleId: Snowflake, on: boolean) {
    const roleIds = on ? [...member.roleIds, roleId] : member.roleIds.filter((id) => id !== roleId);
    try {
      const updated = await api.servers.updateMember(serverId, member.user.id, { roleIds });
      setMembers((prev) => prev.map((m) => (m.user.id === member.user.id ? updated : m)));
    } catch (caught) {
      setError((caught as Error).message);
    }
  }

  async function kick(member: Member) {
    try {
      await api.servers.kick(serverId, member.user.id);
      setMembers((prev) => prev.filter((m) => m.user.id !== member.user.id));
    } catch (caught) {
      setError((caught as Error).message);
    }
  }

  function abrirBanimento(member: Member) {
    setBanindo(member.user.id);
    setMotivo("");
    setDias(0);
    setExpanded(null);
  }

  async function ban(member: Member) {
    const razao = motivo.trim();
    try {
      await api.servers.ban(serverId, member.user.id, {
        ...(razao ? { reason: razao } : {}),
        deleteMessageDays: dias,
      });
      setMembers((prev) => prev.filter((m) => m.user.id !== member.user.id));
      setBanindo(null);
    } catch (caught) {
      setError((caught as Error).message);
    }
  }

  if (busy) return <p className="modal__hint">Carregando…</p>;

  const assignable = roles.filter((r) => !r.isDefault);

  return (
    <div className="settings__form">
      <ul className="members-admin">
        {members.map((member) => {
          const name = member.nickname ?? member.user.displayName ?? member.user.username;
          const moderable = canModerate(member);
          const open = expanded === member.user.id;
          const aBanir = banindo === member.user.id;
          return (
            <li key={member.user.id} className="members-admin__item">
              <div className="members-admin__row">
                <Avatar user={member.user} overrideUrl={member.avatarUrl} size={32} />
                <div className="members-admin__names">
                  <strong>{name}</strong>
                  <small>
                    {member.roleIds.length > 0
                      ? member.roleIds
                          .map((id) => roleById.get(id)?.name)
                          .filter(Boolean)
                          .join(", ") || "Sem cargos"
                      : "Sem cargos"}
                    {member.user.id === ownerId ? " · dono" : ""}
                  </small>
                </div>
                <div className="members-admin__actions">
                  {canAssign && moderable ? (
                    <button
                      type="button"
                      className={`button button--small${open ? " is-active" : ""}`}
                      onClick={() => setExpanded(open ? null : member.user.id)}
                    >
                      Cargos
                    </button>
                  ) : null}
                  {canKick && moderable ? (
                    <button type="button" className="button button--small" onClick={() => void kick(member)}>
                      Expulsar
                    </button>
                  ) : null}
                  {canBan && moderable ? (
                    <button
                      type="button"
                      className={`button button--small icon-button--danger${aBanir ? " is-active" : ""}`}
                      onClick={() => (aBanir ? setBanindo(null) : abrirBanimento(member))}
                    >
                      Banir
                    </button>
                  ) : null}
                </div>
              </div>

              {open && canAssign ? (
                <div className="members-admin__roles">
                  {assignable.map((role) => (
                    <label key={role.id} className="check">
                      <input
                        type="checkbox"
                        checked={member.roleIds.includes(role.id)}
                        onChange={(e) => void assignRole(member, role.id, e.target.checked)}
                      />
                      <span>{role.name}</span>
                    </label>
                  ))}
                </div>
              ) : null}

              {aBanir && canBan ? (
                <div className="members-admin__ban">
                  <label className="field">
                    <span>Motivo (fica no registo)</span>
                    <input
                      value={motivo}
                      maxLength={512}
                      autoFocus
                      placeholder="Opcional"
                      onChange={(e) => setMotivo(e.target.value)}
                    />
                  </label>
                  <label className="field">
                    <span>Apagar mensagens</span>
                    <select value={dias} onChange={(e) => setDias(Number(e.target.value))}>
                      <option value={0}>Não apagar</option>
                      <option value={1}>Do último dia</option>
                      <option value={7}>Dos últimos 7 dias</option>
                    </select>
                  </label>
                  <div className="members-admin__ban-foot">
                    <button
                      type="button"
                      className="button button--small icon-button--danger"
                      onClick={() => void ban(member)}
                    >
                      Banir {name}
                    </button>
                    <button type="button" className="button button--small" onClick={() => setBanindo(null)}>
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      {error ? (
        <p className="auth__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/* ── emojis ───────────────────────────────────────────────── */

/** 2 MB — é o tecto que o servidor aplica ao `kind: "EMOJI"`. */
const EMOJI_MAX_BYTES = 2 * 1024 * 1024;

/**
 * Um nome aceitável a partir do nome do ficheiro. `festa-2024 (1).png` fica
 * `festa_2024_1`. Serve de sugestão e nada mais: o campo continua editável, e
 * quem manda é a validação do servidor.
 */
function nameFromFile(filename: string): string {
  const base = filename.replace(/\.[^.]+$/, "");
  const clean = base
    .replace(/[^a-zA-Z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, Limits.emojiName.max)
    .toLowerCase();
  // O mínimo são dois caracteres; um ficheiro chamado `a.png` não pode deixar o
  // campo num estado que o botão recusa sem explicar porquê.
  return clean.length >= Limits.emojiName.min ? clean : `${clean}_emoji`.slice(0, Limits.emojiName.max);
}

const emojiNameOk = (name: string): boolean =>
  name.length >= Limits.emojiName.min && name.length <= Limits.emojiName.max && Limits.emojiName.pattern.test(name);

/**
 * Emojis personalizados do servidor.
 *
 * O upload é em dois tempos, e é de propósito: o ficheiro vai primeiro para o
 * armazenamento (`uploadImage(file, "EMOJI")`) e só depois é que a API recebe o
 * `fileId`. Assim os bytes nunca passam pela API — e o nome pode ser corrigido
 * enquanto a imagem sobe, sem repetir o upload.
 *
 * A lista sai do store e não de um `useState` local, ao contrário dos cargos:
 * uma mensagem com `<:festa:123>` na conversa por trás desta janela desenha-se a
 * partir do mesmo sítio, e criar um emoji aqui tem de a acertar já.
 */
function EmojisTab({ serverId }: { serverId: Snowflake }) {
  const emojis = useStore((s) => s.emojis);
  const loadEmojis = useStore((s) => s.loadEmojis);
  const createEmoji = useStore((s) => s.createEmoji);
  const renameEmoji = useStore((s) => s.renameEmoji);
  const deleteEmoji = useStore((s) => s.deleteEmoji);

  const list = useMemo<Emoji[]>(() => emojisOf({ emojis }, serverId), [emojis, serverId]);

  const picker = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<{ file: File; preview: string; name: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: Snowflake; name: string } | null>(null);

  useEffect(() => {
    void loadEmojis(serverId).catch((caught: unknown) => setError((caught as Error).message));
  }, [serverId, loadEmojis]);

  // O `objectURL` do ficheiro escolhido é memória do browser até alguém o
  // libertar. Sem isto, escolher dez ficheiros seguidos deixa dez imagens vivas.
  useEffect(() => {
    if (!pending) return;
    return () => URL.revokeObjectURL(pending.preview);
  }, [pending]);

  const full = list.length >= Limits.emojisPerServer;

  function choose(file: File | undefined) {
    if (!file) return;
    setError(null);
    if (!file.type.startsWith("image/")) {
      setError("Só imagens: PNG, JPEG, WebP ou GIF.");
      return;
    }
    if (file.size > EMOJI_MAX_BYTES) {
      setError(`A imagem tem ${Math.round(file.size / 1024)} KB — o limite é 2 MB.`);
      return;
    }
    setPending({ file, preview: URL.createObjectURL(file), name: nameFromFile(file.name) });
  }

  async function confirm() {
    if (!pending || busy) return;
    const name = pending.name.trim();
    if (!emojiNameOk(name)) {
      setError(`O nome tem de ter ${Limits.emojiName.min} a ${Limits.emojiName.max} letras, números ou _.`);
      return;
    }
    if (list.some((e) => e.name.toLowerCase() === name.toLowerCase())) {
      setError(`Já existe um :${name}: neste servidor.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { fileId } = await uploadImage(pending.file, "EMOJI");
      await createEmoji(serverId, name, fileId);
      setPending(null);
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function commitRename() {
    if (!editing) return;
    const name = editing.name.trim();
    const current = list.find((e) => e.id === editing.id);
    // Sair do campo sem mudar nada não é um pedido: pouparia um round-trip que a
    // API já ignora, mas também evita o piscar do erro num nome vazio.
    if (!current || name === current.name) {
      setEditing(null);
      return;
    }
    if (!emojiNameOk(name)) {
      setError(`O nome tem de ter ${Limits.emojiName.min} a ${Limits.emojiName.max} letras, números ou _.`);
      return;
    }
    setError(null);
    try {
      await renameEmoji(serverId, editing.id, name);
      setEditing(null);
    } catch (caught) {
      setError((caught as Error).message);
    }
  }

  async function remove(emoji: Emoji) {
    if (!window.confirm(`Apagar :${emoji.name}:? As mensagens antigas ficam a mostrar o nome em texto.`)) return;
    setError(null);
    try {
      await deleteEmoji(serverId, emoji.id);
    } catch (caught) {
      setError((caught as Error).message);
    }
  }

  return (
    <div className="emojis">
      <header className="emojis__head">
        <div>
          <h3>Emojis personalizados</h3>
          <p className="modal__hint">
            {list.length} de {Limits.emojisPerServer} · PNG, JPEG, WebP ou GIF até 2 MB
          </p>
        </div>
        <button type="button" className="button" disabled={full || busy} onClick={() => picker.current?.click()}>
          <IconPlus size={16} /> Carregar emoji
        </button>
        <input
          ref={picker}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          hidden
          onChange={(e) => {
            choose(e.target.files?.[0]);
            // Limpar o valor deixa escolher OUTRA VEZ o mesmo ficheiro: sem isto
            // o `change` não dispara na segunda tentativa depois de um erro.
            e.target.value = "";
          }}
        />
      </header>

      {full ? <p className="modal__hint">Servidor cheio — apague um emoji para poder carregar outro.</p> : null}
      {error ? (
        <p className="auth__error" role="alert">
          {error}
        </p>
      ) : null}

      {pending ? (
        <div className="emojis__new">
          <img className="emojis__preview" src={pending.preview} alt="Pré-visualização" />
          <label className="emojis__field">
            <span>Nome</span>
            <input
              value={pending.name}
              maxLength={Limits.emojiName.max}
              placeholder="festa"
              onChange={(e) => setPending((p) => (p ? { ...p, name: e.target.value } : p))}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void confirm();
                }
              }}
            />
            <small>Escreve-se :{pending.name.trim() || "nome"}: na conversa.</small>
          </label>
          <div className="emojis__actions">
            <button type="button" className="button button--primary" disabled={busy} onClick={() => void confirm()}>
              {busy ? "A carregar…" : "Criar"}
            </button>
            <button type="button" className="button" disabled={busy} onClick={() => setPending(null)}>
              Cancelar
            </button>
          </div>
        </div>
      ) : null}

      {list.length === 0 ? (
        <p className="modal__hint">Ainda não há nenhum. O que carregar aqui fica disponível para toda a gente.</p>
      ) : (
        <ul className="emojis__grid">
          {list.map((emoji) => (
            <li key={emoji.id} className="emojis__item">
              <img className="emojis__thumb" src={emoji.url} alt={`:${emoji.name}:`} loading="lazy" />
              {editing?.id === emoji.id ? (
                <input
                  className="emojis__rename"
                  value={editing.name}
                  maxLength={Limits.emojiName.max}
                  // biome-ignore lint/a11y/noAutofocus: o campo aparece por um clique deliberado no nome.
                  autoFocus
                  onChange={(e) => setEditing({ id: emoji.id, name: e.target.value })}
                  onBlur={() => void commitRename()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void commitRename();
                    }
                    if (e.key === "Escape") setEditing(null);
                  }}
                />
              ) : (
                <button
                  type="button"
                  className="emojis__name"
                  title="Mudar o nome"
                  onClick={() => setEditing({ id: emoji.id, name: emoji.name })}
                >
                  :{emoji.name}:
                </button>
              )}
              <button
                type="button"
                className="icon-button icon-button--danger"
                title="Apagar"
                onClick={() => void remove(emoji)}
              >
                <IconTrash size={15} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ── banidos ──────────────────────────────────────────────── */

/**
 * O `Avatar` quer um utilizador; a lista de banidos manda três campos soltos.
 * Quem foi banido normalmente já não é membro, portanto o avatar só aparece se
 * a pessoa ainda estiver no cache por outra via — e `OFFLINE` porque um banido
 * não tem presença nenhuma para mostrar.
 */
function comoUtilizador(
  id: Snowflake,
  username: string,
  displayName: string | null,
  avatarUrl: string | null,
): Pick<PublicUser, "id" | "username" | "displayName" | "avatarUrl" | "status"> {
  return { id, username, displayName, avatarUrl, status: "OFFLINE" };
}

function BansTab({ serverId }: { serverId: Snowflake }) {
  const users = useStore((s) => s.users);
  const [bans, setBans] = useState<BanEntry[]>([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aDesbanir, setADesbanir] = useState<Snowflake | null>(null);

  useEffect(() => {
    let alive = true;
    setBusy(true);
    setError(null);
    api.servers
      .bans(serverId)
      .then((list) => {
        if (alive) setBans(list);
      })
      .catch((caught) => {
        if (alive) setError((caught as Error).message);
      })
      .finally(() => {
        if (alive) setBusy(false);
      });
    return () => {
      alive = false;
    };
  }, [serverId]);

  async function unban(userId: Snowflake) {
    setADesbanir(userId);
    setError(null);
    try {
      await api.servers.unban(serverId, userId);
      setBans((prev) => prev.filter((b) => b.userId !== userId));
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setADesbanir(null);
    }
  }

  if (busy) return <p className="modal__hint">Carregando…</p>;
  if (bans.length === 0) return <p className="modal__hint">Ninguém banido.</p>;

  return (
    <div className="settings__form">
      <ul className="bans-admin">
        {bans.map((ban) => {
          const nome = ban.displayName ?? ban.username;
          const quem = users[ban.issuerId];
          return (
            <li key={ban.userId} className="bans-admin__item">
              <Avatar
                user={comoUtilizador(ban.userId, ban.username, ban.displayName, users[ban.userId]?.avatarUrl ?? null)}
                size={32}
              />
              <div className="bans-admin__info">
                <strong>{nome}</strong>
                <small>
                  {formatMessageStamp(ban.createdAt)}
                  {quem ? ` · por ${quem.displayName ?? quem.username}` : ""}
                </small>
                {/* O motivo é o único sítio onde se percebe porquê — sem ele um
                    banimento antigo fica indistinguível de um engano. */}
                <span className="bans-admin__reason">{ban.reason ?? "Sem motivo registado"}</span>
              </div>
              <button
                type="button"
                className="button button--small"
                disabled={aDesbanir === ban.userId}
                onClick={() => void unban(ban.userId)}
              >
                {aDesbanir === ban.userId ? "A desbanir…" : "Desbanir"}
              </button>
            </li>
          );
        })}
      </ul>

      {error ? (
        <p className="auth__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/* ── auditoria ────────────────────────────────────────────── */

const AUDIT_PAGINA = 50;

function AuditTab({ serverId }: { serverId: Snowflake }) {
  const users = useStore((s) => s.users);
  const channels = useStore((s) => s.channels);
  const roles = useStore((s) => s.roles);
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [accao, setAccao] = useState("");
  const [busy, setBusy] = useState(true);
  const [mais, setMais] = useState(false);
  const [fim, setFim] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ids em nomes com o que o cliente já tem: os cargos e canais deste servidor
  // já estão no estado, e os autores das acções são gente que se viu no
  // servidor. Um id que não se conheça cai para o próprio id, não desaparece.
  const nomes: Nomeador = useMemo(
    () => ({
      utilizador: (id) => users[id]?.displayName ?? users[id]?.username ?? null,
      canal: (id) => channels[id]?.name ?? null,
      cargo: (id) => roles[id]?.name ?? null,
    }),
    [users, channels, roles],
  );

  useEffect(() => {
    let alive = true;
    setBusy(true);
    setFim(false);
    setError(null);
    api.servers
      .auditLog(serverId, { limit: AUDIT_PAGINA, ...(accao ? { action: accao } : {}) })
      .then((list) => {
        if (!alive) return;
        setEntries(list);
        setFim(list.length < AUDIT_PAGINA);
      })
      .catch((caught) => {
        if (alive) setError((caught as Error).message);
      })
      .finally(() => {
        if (alive) setBusy(false);
      });
    return () => {
      alive = false;
    };
  }, [serverId, accao]);

  async function carregarMais() {
    const ultimo = entries[entries.length - 1];
    if (!ultimo || mais) return;
    setMais(true);
    try {
      const list = await api.servers.auditLog(serverId, {
        before: ultimo.id,
        limit: AUDIT_PAGINA,
        ...(accao ? { action: accao } : {}),
      });
      setEntries((prev) => [...prev, ...list]);
      if (list.length < AUDIT_PAGINA) setFim(true);
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setMais(false);
    }
  }

  return (
    <div className="settings__form">
      <label className="field">
        <span>Filtrar por acção</span>
        <select value={accao} onChange={(e) => setAccao(e.target.value)}>
          <option value="">Tudo</option>
          {ACCOES_CONHECIDAS.map((a) => (
            <option key={a.id} value={a.id}>
              {a.texto}
            </option>
          ))}
        </select>
      </label>

      {busy ? (
        <p className="modal__hint">Carregando…</p>
      ) : entries.length === 0 ? (
        <p className="modal__hint">Nada registado.</p>
      ) : (
        <ul className="audit">
          {entries.map((entry) => {
            const meta = accaoDe(entry.action);
            const actor = users[entry.actorId];
            const nomeActor = actor ? (actor.displayName ?? actor.username) : "Alguém";
            const alvo = alvoDe(entry, nomes);
            const linhas = alteracoesDe(entry, nomes);
            return (
              <li key={entry.id} className={`audit__item${meta.grave ? " audit__item--grave" : ""}`}>
                <Avatar
                  user={comoUtilizador(
                    entry.actorId,
                    actor?.username ?? "?",
                    actor?.displayName ?? null,
                    actor?.avatarUrl ?? null,
                  )}
                  size={28}
                />
                <div className="audit__body">
                  <p className="audit__line">
                    <strong>{nomeActor}</strong> {meta.texto}
                    {alvo ? <strong> {alvo}</strong> : null}
                  </p>
                  {entry.reason ? <p className="audit__reason">«{entry.reason}»</p> : null}
                  {linhas.length > 0 ? (
                    <ul className="audit__changes">
                      {linhas.map((l) => (
                        <li key={l.campo}>
                          <span>{l.campo}:</span> {l.texto}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  <time className="audit__stamp">{formatMessageStamp(entry.createdAt)}</time>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {!busy && entries.length > 0 && !fim ? (
        <button type="button" className="button button--small" disabled={mais} onClick={() => void carregarMais()}>
          {mais ? "A carregar…" : "Carregar mais"}
        </button>
      ) : null}

      {error ? (
        <p className="auth__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/* ── convites ─────────────────────────────────────────────── */

function InvitesTab({ serverId }: { serverId: Snowflake }) {
  const channels = useStore((s) => s.channels);
  const users = useStore((s) => s.users);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setBusy(true);
    setError(null);
    api.servers
      .invites(serverId)
      .then((list) => {
        if (alive) setInvites(list);
      })
      .catch((caught) => {
        if (alive) setError((caught as Error).message);
      })
      .finally(() => {
        if (alive) setBusy(false);
      });
    return () => {
      alive = false;
    };
  }, [serverId]);

  async function revoke(code: string) {
    try {
      await api.invites.remove(code);
      setInvites((prev) => prev.filter((i) => i.code !== code));
    } catch (caught) {
      setError((caught as Error).message);
    }
  }

  if (busy) return <p className="modal__hint">Carregando…</p>;
  if (invites.length === 0) return <p className="modal__hint">Sem convites ativos.</p>;

  return (
    <div className="settings__form">
      <ul className="invites-admin">
        {invites.map((invite) => {
          const channel = channels[invite.channelId];
          const creator = users[invite.creatorId];
          return (
            <li key={invite.code} className="invites-admin__item">
              <div className="invites-admin__info">
                <strong>{invite.code}</strong>
                <small>
                  {channel ? `#${channel.name}` : "canal removido"} ·{" "}
                  {invite.maxUses ? `${invite.uses}/${invite.maxUses}` : `${invite.uses} usos`} ·{" "}
                  {invite.expiresAt ? `expira ${formatMessageStamp(invite.expiresAt)}` : "não expira"}
                  {creator ? ` · por ${creator.displayName ?? creator.username}` : ""}
                </small>
              </div>
              <button
                type="button"
                className="button button--small icon-button--danger"
                onClick={() => void revoke(invite.code)}
              >
                Revogar
              </button>
            </li>
          );
        })}
      </ul>

      {error ? (
        <p className="auth__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
